import React from 'react';
import { Empty } from 'antd';
import { t } from '../i18n/t';

/**
 * THE no-data state for the whole app. It is also antd's global `renderEmpty` (LocaleProvider), so
 * every Table, List, Select and Transfer with nothing to show renders this one component - change
 * it here and every empty screen changes with it. Use it directly for a custom empty block:
 *   <NoData title={t('x:noRequests')} description={t('x:noRequestsHint')} action={<Button/>} />
 */
export interface NoDataProps {
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  /** `small` for dropdowns and tight panels. */
  size?: 'default' | 'small';
}

const NoData: React.FC<NoDataProps> = ({ title, description, action, size = 'default' }) => (
  <Empty
    className={`no-data no-data--${size}`}
    image={Empty.PRESENTED_IMAGE_SIMPLE}
    description={
      <>
        <span className="no-data__title">{title ?? t('common:state.noData')}</span>
        {description && <span className="no-data__body">{description}</span>}
      </>
    }
  >
    {action}
  </Empty>
);

const COMPACT = new Set(['Select', 'TreeSelect', 'Cascader', 'Transfer', 'AutoComplete', 'Mentions']);

/** antd `ConfigProvider.renderEmpty`: one empty state for every antd component. */
export const renderNoData = (componentName?: string): React.ReactNode => (
  <NoData size={componentName && COMPACT.has(componentName) ? 'small' : 'default'} />
);

export default NoData;
